/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailMembers.ts
 * FILE_ID: EMAILHUB-USEEMAILMEMBERS-001
 *
 * PURPOSE: Resolve the org's teammate roster and return the plain
 *          `AllocationMember[]` slice that the Email→Quote ARC § Phase 10
 *          components (`EmailAllocationRail`, `EmailContextMenuProvider`
 *          invite submenu) expect.
 *
 * WHY:     Both Phase 10 surface components are purely declarative
 *          (props in, JSX out) — they shouldn't know about session
 *          discovery, 403 stale-cache recovery, or the kanban-host
 *          adapter shape. This hook is the single place we couple
 *          email-hub to the kanban-host members adapter so the
 *          coupling stays auditable.
 *
 *          BUG-2026-07-15-MEMBERS-FETCH-STORM: this hook previously
 *          fetched via a raw useState/useEffect keyed on a `useMemo`
 *          whose deps included the SESSION OBJECT IDENTITY. Hosts build
 *          the session inline per render (`buildSession(...)`), so every
 *          shell re-render (notification poll every 15s, token refresh)
 *          minted a new adapter and fired a NEW, non-deduped, non-aborted
 *          GET /api/organisations/<id>/members/. With four mounted
 *          consumers and a 25s fetch timeout, requests spawned faster
 *          than they completed, saturating Chrome's per-origin connection
 *          pool — net::ERR_INSUFFICIENT_RESOURCES took down notifications
 *          and telemetry POSTs too. Now a react-query read keyed on the
 *          PRIMITIVE org id: all mounts and re-renders collapse into one
 *          in-flight request, cached 5 min.
 *
 *          INVARIANT: a hook must never issue a network request merely
 *          because a parent re-rendered; identical concurrent reads
 *          dedupe to one in-flight request.
 *
 *          Returns `null` (not `[]`) while loading so the caller
 *          can suppress the rail entirely on a cold page — once
 *          we have a definitive answer (even if empty), we
 *          return an array. Callers do
 *          `{members != null && <EmailAllocationRail members={members} />}`.
 *
 * RELATED:
 *   - packages/kanban-host/src/kanbanMembersAdapter.ts: adapter source
 *   - packages/email-hub/src/feature/components/EmailAllocationRail.tsx
 *   - packages/email-hub/src/feature/components/EmailContextMenu.tsx
 */

import { useQuery } from '@tanstack/react-query'

import { buildMembersAdapter, resolveKanbanOrgId } from '@quotech/kanban-host'
import { getTeamMembers } from '../../_stubs/settings/quotechSettings'

import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import {
  getQuotechOrgIdOverride,
  getRuntimeQuotechOrgId,
} from '../../_internal/services/quotechApi'

import type { AllocationMember } from '../components/EmailAllocationRail'

/** Query key builder — exported so tests (and any future invalidation
 *  after a member add) target the exact cache slot. Keyed on the
 *  PRIMITIVE org id only: re-renders with a fresh session object but the
 *  same org must hit the same cache entry (the fetch-storm invariant). */
export function emailAllocationMembersKey(orgId: number) {
  return ['email-allocation-members', orgId] as const
}

/** Roster cache freshness — mirrors the kanban board's members query
 *  (packages/ui board-view useBoardOrgQueries): members are managed in
 *  People/Team, not on email surfaces, so 5 min staleness is safe. */
export const EMAIL_ALLOCATION_MEMBERS_STALE_MS = 5 * 60_000

/** Resolve the active org's teammates for the Phase 10 allocation
 *  rail + right-click invite submenu.
 *
 *  Returns:
 *    - `null` while resolution is in flight (no org id yet OR the
 *      first fetch hasn't settled),
 *    - `AllocationMember[]` once we have an answer (may be empty
 *      if the org has no other members or the BE 403/404'd —
 *      the adapter swallows those and returns `[]`).
 *
 *  Re-resolves when the session's org id changes (e.g. user
 *  switches tenant in the org-picker) — the query key carries the id.
 */
export function useEmailAllocationMembers(): AllocationMember[] | null {
  const { session } = useApiAuth()
  const orgId = resolveKanbanOrgId(session, {
    runtimeId: getRuntimeQuotechOrgId(),
    buildtimeId: getQuotechOrgIdOverride(),
  })
  // Primitive slice of the session — the no-op refresh below needs it,
  // and using the primitive (not the session object) keeps the queryFn
  // capture stable in spirit with the storm invariant above.
  const sessionOrgId = session?.organisation ? (session.organisation.id ?? undefined) : null

  const query = useQuery<AllocationMember[]>({
    queryKey: emailAllocationMembersKey(orgId ?? -1),
    queryFn: async () => {
      const adapter = buildMembersAdapter(orgId, {
        fetchMembers: getTeamMembers,
        // email-hub's ApiAuthGate intentionally exposes `session` ONLY
        // (email pages read, never refresh — see kanban-host
        // _internal/auth/useApiAuth doc). The members-adapter requires a
        // `refreshSession` for its 403 stale-cache retry; we supply the
        // CURRENT org as a no-op "refresh" so the adapter degrades to a
        // single same-org retry → empty rail (same observable outcome as
        // before, type-safe and non-throwing).
        refreshSession: async () => ({
          organisation: sessionOrgId === null ? null : { id: sessionOrgId },
        }),
      })
      if (!adapter) return []
      const list = await adapter.listMembers()
      // `KanbanMember` is structurally compatible with `AllocationMember`
      // — id/name/email/avatar_url all align. The slice is intentional so
      // the rail doesn't accidentally depend on `role` (which we don't
      // surface in the rail today and may grow / shrink independently).
      return list.map((m) => ({
        id: m.id,
        name: m.name,
        email: m.email,
        avatar_url: m.avatar_url ?? null,
      }))
    },
    enabled: orgId !== undefined,
    staleTime: EMAIL_ALLOCATION_MEMBERS_STALE_MS,
    refetchOnWindowFocus: false,
    // The adapter already maps every known failure (403/404/timeout/5xx)
    // to `[]`; a queryFn error is a truly unexpected throw. Never retry —
    // retries during a stall are exactly how the storm amplified.
    retry: false,
  })

  if (orgId === undefined) return null
  if (query.isError) {
    // Unexpected runtime throw — fall back to `[]` (not `null`) so the
    // rail renders with disabled pickers instead of perma-skeleton.
    return []
  }
  return query.data ?? null
}
